import React, { useEffect } from "react";

import {
  ConnectionLineType,
  MiniMap,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";

import { Box, Button, Typography } from "@mui/material";
import { useControls } from "leva";
import { useAutoLayout } from "@/diagramme/hooks";

import { proOptions, defaultEdgeOptions } from "@/diagramme/config/reactflow";
import { kNodeTypes } from "@/diagramme/components/nodes";

import { workflow2reactflow, groupNodes } from "@/diagramme/utils";

import { DIAGRAMME_ALGORITHM } from "@/diagramme/config";

import type { Node as NodeType, Edge as EdgeType } from "@xyflow/react";
import type { LayoutOptions } from "@/diagramme/hooks/auto-layout";
import type { FiliationTree, FiliationNode, FiliationEvent, FiliationEventWithFiliationStateGroupedByDate } from "@/diagramme/types";

import "@xyflow/react/dist/style.css";
import "@/diagramme/index.css";

const NODE_TYPES = kNodeTypes;

interface ChosenFiliationData extends FiliationTree {
  originesByDate: Record<string, { nodes: FiliationNode[] }>;
}

interface FlowProps {
  chosenFiliationData: ChosenFiliationData | null;
  currentDate: string;
  filiationStatesByEvents: FiliationEventWithFiliationStateGroupedByDate[] | null;
  panelOpenState: boolean;
  previousChosenFiliationData: ChosenFiliationData | null;
  previousCurrentDate: string;
  overviewData?: { nodes: FiliationNode[]; origines: FiliationEvent[]; origines_by_date?: any; cadastre_filiation_diagramme?: any; fullData?: any } | null;
}

/**
 * This example shows how you can automatically arrange your nodes after adding child nodes to your graph.
 */
export function Flow({
  chosenFiliationData,
  currentDate,
  filiationStatesByEvents,
  panelOpenState,
  overviewData,
}: FlowProps) {
  /**
   * @description State hooks for managing nodes in the flow diagram.
   * Don't directly mutate these states, use the provided layoutReactflow function instead.
   *
   * @returns {Array} nodes - Array of nodes in the flow diagram.
   */
  const [nodes, setNodes, onNodesChange] = useNodesState<NodeType>([]);

  /**
   * @description State hooks for managing edges in the flow diagram.
   * Don't directly mutate these states, use the provided layoutReactflow function instead.
   *
   * @returns {Array} nodes - Array of edges in the flow diagram.
   */
  const [edges, setEdges, onEdgesChange] = useEdgesState<EdgeType>([]);

  const { fitView } = useReactFlow();

  // 👇 This hook is used to display a leva (https://github.com/pmndrs/leva) control panel for this example.
  // You can safely remove it, if you don't want to use it.
  const layoutOptions = useControls({
    algorithm: {
      value: DIAGRAMME_ALGORITHM as LayoutOptions["algorithm"],
      options: ["dagre", "d3-hierarchy", "elk"] as LayoutOptions["algorithm"][],
    },
    direction: {
      value: "TB" as LayoutOptions["direction"],
      options: {
        down: "TB",
        right: "LR",
        up: "BT",
        left: "RL",
      } as Record<string, LayoutOptions["direction"]>,
    },
    spacing: [80, 80],
  });

  /**
   * @description Convertit les données en format ReactFlow SANS groupement (pour la vue d'ensemble)
   * Construit l'arbre basé sur origines_by_date : plus ancien en haut, plus récent en bas
   */
  const overviewDataToReactFlow = (data: any) => {
    if (!data?.origines_by_date) {
      return { nodes: [], edges: [] };
    }
    
    // Trier les dates chronologiquement
    const dates = Object.keys(data.origines_by_date).sort((a, b) => {
      const [dayA, monthA, yearA] = a.split('/').map(Number);
      const [dayB, monthB, yearB] = b.split('/').map(Number);
      return new Date(yearA, monthA - 1, dayA).getTime() - new Date(yearB, monthB - 1, dayB).getTime();
    });
    
    // Collecter tous les cadastres uniques par VALEUR (nom)
    const cadastresByValue = new Map<string, any>();
    let commune: any = null;
    
    // Trouver la commune dans data.nodes
    const nodesData = data?.nodes || [];
    const communeNode = nodesData.find((n: any) => n.type === 'commune');
    if (communeNode) {
      commune = { ...communeNode };
    }
    
    // Collecter les cadastres de chaque date
    dates.forEach((date) => {
      const dateData = data.origines_by_date[date];
      const cadastresAtDate = dateData?.nodes?.filter((n: any) => n.type === 'cadastre') || [];
      cadastresAtDate.forEach((c: any) => {
        if (!cadastresByValue.has(c.value)) {
          cadastresByValue.set(c.value, { ...c, firstDate: date });
        }
      });
    });
    
    // Déterminer quels cadastres sont dans la dernière date (existants) vs supprimés
    const lastDateData = data.origines_by_date[dates[dates.length - 1]];
    const lastDateValues = new Set(
      (lastDateData?.nodes?.filter((n: any) => n.type === 'cadastre') || []).map((c: any) => c.value)
    );
    
    
    // Créer les nœuds React Flow
    const NODE_HEIGHT = 32;
    const nodes: any[] = [];
    
    // Calculer la largeur dynamique basée sur le texte
    const getNodeWidth = (text: string) => {
      const baseWidth = 80;
      const charWidth = 7; // Estimation pour font-size 11px
      const calculatedWidth = baseWidth + (text?.length || 0) * charWidth;
      return Math.max(120, Math.min(calculatedWidth, 300)); // Min 120px, Max 300px
    };
    
    // Ajouter la commune
    if (commune) {
      const communeWidth = getNodeWidth(commune.value);
      nodes.push({
        id: commune.id.toString(),
        data: { ...commune, isOverview: true, isLeafNode: false, is_suppressed: false },
        position: { x: 0, y: 0 },
        type: 'base',
        measured: { width: communeWidth, height: NODE_HEIGHT },
        style: { width: communeWidth, background: 'transparent', border: 'none', boxShadow: 'none' },
      });
    }
    
    // Ajouter les cadastres avec un ID unique basé sur la valeur
    let nodeIdCounter = 10000; // Utiliser des IDs uniques
    const valueToNodeId = new Map<string, string>();
    
    cadastresByValue.forEach((cadastre, value) => {
      const nodeId = (nodeIdCounter++).toString();
      valueToNodeId.set(value, nodeId);
      const isSuppressed = !lastDateValues.has(value);
      const nodeWidth = getNodeWidth(value);
      
      nodes.push({
        id: nodeId,
        data: { 
          ...cadastre, 
          isOverview: true, 
          isLeafNode: !isSuppressed,
          is_suppressed: isSuppressed,
        },
        position: { x: 0, y: 0 },
        type: 'base',
        measured: { width: nodeWidth, height: NODE_HEIGHT },
        style: { width: nodeWidth, background: 'transparent', border: 'none', boxShadow: 'none' },
      });
    });
    
    // Créer les edges basés sur les transitions temporelles
    const edges: any[] = [];
    const edgeSet = new Set<string>();
    
    // Commune → cadastres de la première date
    if (commune && dates.length > 0) {
      const firstDateData = data.origines_by_date[dates[0]];
      const firstCadastres = firstDateData?.nodes?.filter((n: any) => n.type === 'cadastre') || [];
      firstCadastres.forEach((c: any) => {
        const targetId = valueToNodeId.get(c.value);
        if (targetId) {
          const edgeId = `${commune.id}->${targetId}`;
          if (!edgeSet.has(edgeId)) {
            edgeSet.add(edgeId);
            edges.push({ id: edgeId, source: commune.id.toString(), target: targetId });
          }
        }
      });
    }
    
    // Pour chaque transition de date
    for (let i = 0; i < dates.length - 1; i++) {
      const currentDateData = data.origines_by_date[dates[i]];
      const nextDateData = data.origines_by_date[dates[i + 1]];
      
      const currentCadastres = (currentDateData?.nodes?.filter((n: any) => n.type === 'cadastre') || []) as any[];
      const nextCadastres = (nextDateData?.nodes?.filter((n: any) => n.type === 'cadastre') || []) as any[];
      
      const currentValues = new Set<string>(currentCadastres.map((c) => c.value as string));
      const nextValues = new Set<string>(nextCadastres.map((c) => c.value as string));
      
      // Cadastres qui disparaissent (dans current mais pas dans next)
      const disappearing: string[] = [];
      currentValues.forEach((v) => { if (!nextValues.has(v)) disappearing.push(v); });
      // Cadastres qui apparaissent (dans next mais pas dans current)
      const appearing: string[] = [];
      nextValues.forEach((v) => { if (!currentValues.has(v)) appearing.push(v); });
      
      // Créer les edges : chaque cadastre qui disparaît → chaque cadastre qui apparaît
      disappearing.forEach((parentValue: string) => {
        const parentId = valueToNodeId.get(parentValue);
        if (!parentId) return;
        
        appearing.forEach((childValue: string) => {
          const childId = valueToNodeId.get(childValue);
          if (!childId) return;
          
          const edgeId = `${parentId}->${childId}`;
          if (!edgeSet.has(edgeId)) {
            edgeSet.add(edgeId);
            edges.push({ id: edgeId, source: parentId, target: childId });
          }
        });
      });
    }
    
    return { nodes, edges };
  };

  /**
   * @description Effect reacting to currentDate change and triggering computation of the new filiation state for the current date.
   */
  useEffect(() => {
    // Mode "Vue d'ensemble" - utiliser les données SANS groupement
    if (currentDate === "overview" && overviewData) {
      const { nodes, edges } = overviewDataToReactFlow(overviewData);
      setNodes([...nodes]);
      setEdges([...edges]);
      return;
    }

    if (!chosenFiliationData) return;

    if (filiationStatesByEvents && currentDate) {
      const filiationStateForCurrentDate = [...filiationStatesByEvents].find(
        (filiationStateByDate: FiliationEventWithFiliationStateGroupedByDate) =>
          filiationStateByDate.date === currentDate
      );

      if (filiationStateForCurrentDate && chosenFiliationData && chosenFiliationData.originesByDate) {
        // Pour la vue par date : tous les nœuds qui existent à cette date sont "existant"
        // Le style "supprimé" s'applique uniquement en mode Vue d'ensemble
        const currentNodes = chosenFiliationData.originesByDate[`${currentDate}`]?.nodes || [];
        const nodesWithSuppressedFlag = currentNodes.map((node: any) => ({
          ...node,
          is_suppressed: false, // Tous les nœuds visibles à cette date existent à ce moment
        }));

        const { nodes, edges } = workflow2reactflow({
          nodes: nodesWithSuppressedFlag,
          origines: chosenFiliationData.origines,
        });

        const groupedNodes = groupNodes(nodes);
        setNodes([...groupedNodes]);
        setEdges([...edges]);
      }
    }
  }, [filiationStatesByEvents, chosenFiliationData, currentDate, setNodes, setEdges, groupNodes, overviewData]);

  /**
   * @description Handles the computation of the layout once the elements or the direction changes
   */
  useAutoLayout(layoutOptions);

  /**
   * @description Every time our nodes change, this hook center the graph again.
   */
  useEffect(() => {
    fitView();
  }, [nodes, edges, fitView, panelOpenState]);

  const hasOverviewDiagramData =
    currentDate === "overview" &&
    !!overviewData?.origines_by_date &&
    Object.keys(overviewData.origines_by_date).length > 0 &&
    (overviewData.nodes?.length ?? 0) > 0;

  const isDiagramEmpty =
    !hasOverviewDiagramData &&
    (!chosenFiliationData ||
      !filiationStatesByEvents ||
      filiationStatesByEvents.length === 0 ||
      nodes.length === 0);

  return (
    <>
      {isDiagramEmpty ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            padding: 4,
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              maxWidth: "450px",
              padding: 3,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              Aucun diagramme n'est encore disponible
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Nous n'avons pas encore détecté de formalité permettant la création du diagramme (ex : division parcellaire, état descriptif de division, etc …)
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Si vous venez de sélectionner le ou les biens objet de votre dossier, veuillez patienter, l'analyse est en cours.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.close()}
              sx={{
                borderRadius: "10px",
              }}
            >
              Compris
            </Button>
          </Box>
        </Box>
      ) : (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          connectionLineType={ConnectionLineType.SmoothStep}
          defaultEdgeOptions={defaultEdgeOptions}
          nodesDraggable={false}
          nodesConnectable={false}
          onEdgesChange={onEdgesChange}
          onNodesChange={onNodesChange}
          proOptions={proOptions}
          zoomOnDoubleClick={false}
          panOnDrag={false}
          panOnScroll={false}
          zoomOnPinch={false}
          fitView
          fitViewOptions={{ padding: 0.3, includeHiddenNodes: false }}
        >
          <MiniMap 
            nodeStrokeWidth={3}
            nodeColor={(node) => {
              const nodeType = node.data?.type;
              const isSuppressed = node.data?.is_suppressed === true;
              if (nodeType === "commune") return "#9e9e9e";
              if (nodeType === "cadastre") return isSuppressed ? "#81c784" : "#4caf50";
              if (nodeType === "volume") return isSuppressed ? "#ffb74d" : "#f57c00";
              if (nodeType === "lot") return isSuppressed ? "#64b5f6" : "#1976d2";
              return "#9e9e9e";
            }}
          />
        </ReactFlow>
      )}
    </>
  );
}
